import { InputAdornment } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import FieldRow from 'components/FormElements/FieldRow';
import TextField from 'components/FormElements/AppTextInput';
import { baseDomain } from 'adminConstants/redirectConstant';
import ColourPicker from './ColourPicker';
import Spacing from 'components/Spacing';
import CkEditor from 'components/CkEditor';
import api from 'lib/trpcClient';
import { useParams, Link } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { eisFirm as rules } from 'further-ui/validations';
import useStyles from '../commonStyles';
import { useNotification } from 'hooks/ui/useNotification';
import { Grid } from '@mui/material';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import { FIRM } from 'adminConstants/routes';
import PageSection from 'components/PageSection';
import Dropzone from 'components/Dropzone';
import { UploadType } from 'further-types/files';
import useFileUpload from 'hooks/ui/useFileUpload';
import { useEffect, useState } from 'react';
import { RolePermission } from 'further-types/admin-user-role';
import { useHasPermissions } from 'hooks/ui/usePermissions';

type FormValues = z.infer<typeof rules.UpdateEisFirmWhiteLabelSettingsInput>;

const WhitelabellingSettings: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { success, error } = useNotification();
  const { classes } = useStyles();
  const { uploadFile } = useFileUpload();
  const apiUtils = api.useUtils();
  const [canEditFirm] = useHasPermissions([RolePermission.EditFirm]);

  const [previewImages, setPreviewImages] = useState<{
    firmLogo: string;
    investmentFirmLogo: string;
    whiteLabelConfig: {
      whiteBgLogo: {
        url: string;
        key: string;
      };
      colorBgLogo: {
        url: string;
        key: string;
      };
    };
  }>({
    firmLogo: '',
    investmentFirmLogo: '',
    whiteLabelConfig: {
      whiteBgLogo: {
        url: '',
        key: '',
      },
      colorBgLogo: {
        url: '',
        key: '',
      },
    },
  });

  const { mutate: updateEisFirm, isPending: isUpdating } =
    api.eisFirm.updateWhiteLabelSettings.useMutation({
      onSuccess: () => {
        success('Firm updated successfully.');
        apiUtils.eisFirm.getById.invalidate();
      },
      onError: (err) => {
        error(err?.message ?? 'Something went wrong.');
      },
    });

  const { data: eisFirm } = api.eisFirm.getById.useQuery(
    { firmId: id },
    { enabled: !!id },
  );

  const formMethods = useForm<FormValues>({
    shouldFocusError: true,
    resolver: zodResolver(rules.UpdateEisFirmWhiteLabelSettingsInput),
    values: {
      firmId: id ?? '',
      whiteLabelConfig: {
        bgColor: eisFirm?.whiteLabelConfig?.bgColor ?? '',
        bgColorContrastText:
          eisFirm?.whiteLabelConfig?.bgColorContrastText ?? '',
        lightBackgroundIconColor:
          eisFirm?.whiteLabelConfig?.lightBackgroundIconColor ?? '',
        bgColorAlternate: eisFirm?.whiteLabelConfig?.bgColorAlternate ?? '',
        bgColorAlternateContrastText:
          eisFirm?.whiteLabelConfig?.bgColorAlternateContrastText ?? '',
        primaryColor: eisFirm?.whiteLabelConfig?.primaryColor ?? '',
        primaryContrastText:
          eisFirm?.whiteLabelConfig?.primaryContrastText ?? '',
        primaryHoverColor: eisFirm?.whiteLabelConfig?.primaryHoverColor ?? '',
        secondaryColor: eisFirm?.whiteLabelConfig?.secondaryColor ?? '',
        clientServiceEmail: eisFirm?.whiteLabelConfig?.clientServiceEmail ?? '',
        emailFooterText: eisFirm?.whiteLabelConfig?.emailFooterText ?? '',
        portalFooterText: eisFirm?.whiteLabelConfig?.portalFooterText ?? '',
        whiteBgLogo: {
          url: eisFirm?.whiteLabelConfig?.whiteBgLogo?.url ?? '',
          key: eisFirm?.whiteLabelConfig?.whiteBgLogo?.key ?? '',
        },
        colorBgLogo: {
          url: eisFirm?.whiteLabelConfig?.colorBgLogo?.url ?? '',
          key: eisFirm?.whiteLabelConfig?.colorBgLogo?.key ?? '',
        },
      },
      contactDetails: {
        keyChecks: eisFirm?.contactDetails?.keyChecks ?? [],
        phoneHours: eisFirm?.contactDetails?.phoneHours ?? '',
        phoneNo: eisFirm?.contactDetails?.phoneNo ?? '',
        email: eisFirm?.contactDetails?.email ?? '',
      },
      domain: eisFirm?.domain ?? '',
      firmLogo: eisFirm?.firmLogo ?? '',
      investmentFirmLogo: eisFirm?.investmentFirmLogo ?? '',
    },
    criteriaMode: 'all',
    reValidateMode: 'onSubmit',
  });

  const handleImageUpload = async (files, onSuccess) => {
    if (!files?.[0]?.path) return;
    const uploadResult = await uploadFile(files[0], UploadType.FirmLogos);
    if (uploadResult.error) {
      error(uploadResult.error.message);
    } else {
      const { url, filePath } = uploadResult.data;
      onSuccess(url, filePath);
    }
  };

  const { handleSubmit, control, setValue } = formMethods;

  const onSubmit = async (data) => {
    await updateEisFirm({ ...data, id });
  };

  const getPreviewFiles = (url?: string | null) => {
    return url ? [{ preview: url, fileName: '' }] : [];
  };

  const onDropFile = (key: string) => async (files: Array<File>) => {
    await handleImageUpload(files, (url, filePath) => {
      setValue(key as keyof FormValues, filePath);

      // Handle nested paths properly for previews
      if (key.includes('.')) {
        const [parent, child] = key.split('.');

        setPreviewImages((prev) => {
          if (parent === 'whiteLabelConfig') {
            return {
              ...prev,
              whiteLabelConfig: {
                ...prev.whiteLabelConfig,
                [child]: {
                  url,
                  key: url,
                },
              },
            };
          }
          return prev;
        });
      } else {
        setPreviewImages((prev) => ({
          ...prev,
          [key]: url,
        }));
      }
    });
  };

  const onRemove = (key: string) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');

      setValue(`${parent}.${child}.key` as keyof FormValues, '');
      setValue(`${parent}.${child}.url` as keyof FormValues, '');

      setPreviewImages((prev) => ({
        ...prev,
        whiteLabelConfig: {
          ...prev.whiteLabelConfig,
          [child]: {
            url: '',
            key: '',
          },
        },
      }));
    } else {
      setValue(key as keyof FormValues, '');

      setPreviewImages((prev) => ({
        ...prev,
        [key]: '',
      }));
    }
  };

  useEffect(() => {
    if (eisFirm) {
      setPreviewImages({
        firmLogo: eisFirm.firmLogoPreview ?? '',
        investmentFirmLogo: eisFirm.investmentFirmLogoPreview ?? '',
        whiteLabelConfig: {
          whiteBgLogo: {
            url: eisFirm.whiteLabelConfig?.whiteBgLogo?.url ?? '',
            key: eisFirm.whiteLabelConfig?.whiteBgLogo?.key ?? '',
          },
          colorBgLogo: {
            url: eisFirm.whiteLabelConfig?.colorBgLogo?.url ?? '',
            key: eisFirm.whiteLabelConfig?.colorBgLogo?.key ?? '',
          },
        },
      });
    }
  }, [eisFirm]);

  return (
    <FormProvider {...formMethods}>
      <form>
        <Spacing size="lg">
          <PageSection title="Firm logos">
            <Spacing>
              <FieldRow
                title="PDF update front page logo"
                informationText="(Primary colour background)"
              >
                <Controller
                  name="firmLogo"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, ...rest } }) => (
                    <Dropzone
                      {...rest}
                      ref={ref}
                      defaultAcceptGroup="images"
                      onDropFile={onDropFile('firmLogo')}
                      files={getPreviewFiles(previewImages.firmLogo)}
                      onRemove={() => onRemove('firmLogo')}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Favicon logo"
                informationText="(Square only, any colour background)"
              >
                <Controller
                  name="investmentFirmLogo"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, ...rest } }) => (
                    <Dropzone
                      {...rest}
                      ref={ref}
                      defaultAcceptGroup="images"
                      onDropFile={onDropFile('investmentFirmLogo')}
                      files={getPreviewFiles(previewImages.investmentFirmLogo)}
                      onRemove={() => onRemove('investmentFirmLogo')}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Portal and email header logo"
                informationText="(Transparent/no background)"
              >
                <Controller
                  name="whiteLabelConfig.whiteBgLogo.key"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, ...rest } }) => (
                    <Dropzone
                      {...rest}
                      ref={ref}
                      defaultAcceptGroup="images"
                      onDropFile={onDropFile(
                        'whiteLabelConfig.whiteBgLogo.key',
                      )}
                      files={getPreviewFiles(
                        previewImages?.whiteLabelConfig?.whiteBgLogo?.url,
                      )}
                      onRemove={() => {
                        onRemove('whiteLabelConfig.whiteBgLogo.key');
                      }}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Subscription flow logo"
                informationText="(Transparent or primary colour background)"
              >
                <Controller
                  name="whiteLabelConfig.colorBgLogo.key"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, ...rest } }) => (
                    <Dropzone
                      {...rest}
                      ref={ref}
                      defaultAcceptGroup="images"
                      onDropFile={onDropFile(
                        'whiteLabelConfig.colorBgLogo.key',
                      )}
                      files={getPreviewFiles(
                        previewImages?.whiteLabelConfig?.colorBgLogo?.url,
                      )}
                      onRemove={() =>
                        onRemove('whiteLabelConfig.colorBgLogo.key')
                      }
                    />
                  )}
                />
              </FieldRow>
            </Spacing>
          </PageSection>
          <PageSection title="White labelling settings" showDivider>
            <Spacing>
              <FieldRow
                title="Background (primary) colour"
                centerTitle
                tooltipText={
                  <>
                    This is the colour used as the subscription flow background
                    and the PDF report front page. This should be a key colour
                    from your brand’s colour palette and should contrast with
                    your <i>Subscription flow logo</i> above. Dark colours work
                    much better than light colours, as some areas of the site
                    are white.
                  </>
                }
              >
                <Controller
                  name="whiteLabelConfig.bgColor"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <ColourPicker {...rest} ref={ref} />
                  )}
                />
              </FieldRow>

              <FieldRow
                title="PDF front page font colour"
                centerTitle
                tooltipText="This depends on the background colour above. PDF text should be visible against the background colour (e.g. a light background colour requires a dark text colour)."
              >
                <Controller
                  name="whiteLabelConfig.bgColorContrastText"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <ColourPicker {...rest} ref={ref} />
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Portal tile/table colour"
                centerTitle
                tooltipText="This controls the colour of portal tiles and table column heading rows. Dark colours work with no issues. While a dark pastel colour may be used, very light or pastel colours will not work against white areas of the portal."
              >
                <Controller
                  name="whiteLabelConfig.bgColorAlternate"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <ColourPicker {...rest} ref={ref} />
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Portal tile/table font colour"
                centerTitle
                tooltipText={
                  <>
                    This colour should contrast with the{' '}
                    <i>Portal tile/table colour</i> selected above (white is
                    best for a dark colour, or black for a light colour).
                  </>
                }
              >
                <Controller
                  name="whiteLabelConfig.bgColorAlternateContrastText"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <ColourPicker {...rest} ref={ref} />
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Portal button/text colour"
                centerTitle
                tooltipText={
                  <>
                    This is the colour of the buttons and hyperlinked text on
                    your site. It will ideally match your{' '}
                    <i>Portal tile/table colour</i>. Dark colours work better
                    than light colours as often text backgrounds are white.
                  </>
                }
              >
                <Controller
                  name="whiteLabelConfig.primaryColor"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <ColourPicker {...rest} ref={ref} />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Portal button/text hover colour"
                centerTitle
                tooltipText={
                  <>
                    This colour should be either lighter or darker than your{' '}
                    <i>Portal button/text colour</i>, to signify when the user
                    is hovering on a button.
                  </>
                }
              >
                <Controller
                  name="whiteLabelConfig.primaryHoverColor"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <ColourPicker {...rest} ref={ref} />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Portal button font colour"
                centerTitle
                tooltipText={
                  <>
                    This colour should contrast with the{' '}
                    <i>Portal button/text colour</i> selected above (white is
                    best for a dark colour, or black for a light colour).
                  </>
                }
              >
                <Controller
                  name="whiteLabelConfig.primaryContrastText"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <ColourPicker {...rest} ref={ref} />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Portal menu icon colour"
                centerTitle
                tooltipText={
                  <>
                    This controls the colour of the menu hamburger (three line
                    menu icon) at the top of the portal. This should typically
                    be aligned with a colour in your{' '}
                    <i>Portal and email header logo</i> above.
                  </>
                }
              >
                <Controller
                  name="whiteLabelConfig.lightBackgroundIconColor"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <ColourPicker {...rest} ref={ref} />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Onboarding flow menu icon colour"
                centerTitle
                tooltipText={
                  <>
                    This controls the colour of the menu hamburger (three line
                    menu icon) during the investment flow only. This should
                    contrast with your background colour and will often match a
                    colour used in your <i>Subscription flow logo</i> above.
                  </>
                }
              >
                <Controller
                  name="whiteLabelConfig.secondaryColor"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <ColourPicker {...rest} ref={ref} />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Onboarding side panel text"
                centerTitle
                tooltipText="All investors and advisers will see an informational side panel during your fund's subscription flow. This side panel contains your contact details as well as three optional bullets (with a tick icon next to each bullet). You may use these bullets to provide advice, fund benefits or other brief information if you wish."
              >
                <Controller
                  name={`contactDetails.keyChecks.0`}
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, ...rest } }) => (
                    <TextField
                      {...rest}
                      inputRef={ref}
                      placeholder="First bullet point, e.g. Subscription specialists since 2002"
                      fullWidth
                    />
                  )}
                />
              </FieldRow>
              <FieldRow title=" ">
                <Controller
                  name={`contactDetails.keyChecks.1`}
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, ...rest } }) => (
                    <TextField
                      {...rest}
                      inputRef={ref}
                      placeholder="Second bullet point, e.g. Managing over £2bn of investors' capital"
                      fullWidth
                    />
                  )}
                />
              </FieldRow>
              <FieldRow title=" ">
                <Controller
                  name={`contactDetails.keyChecks.2`}
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, ...rest } }) => (
                    <TextField
                      {...rest}
                      inputRef={ref}
                      placeholder="Third bullet point, e.g. Supporting over 200 high growth companies"
                      fullWidth
                    />
                  )}
                />
              </FieldRow>
              <FieldRow title="Contact days and times" centerTitle>
                <Controller
                  name="contactDetails.phoneHours"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <TextField
                      {...rest}
                      inputRef={ref}
                      placeholder="When investors can contact you, e.g. Mon-Fri 9:00-17:00"
                      fullWidth
                      helperText={error?.message}
                      error={!!error}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow title="Investor service phone number" centerTitle>
                <Controller
                  name="contactDetails.phoneNo"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <TextField
                      {...rest}
                      inputRef={ref}
                      placeholder="Enter investor service phone number"
                      fullWidth
                      helperText={error?.message}
                      error={!!error}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Verified reply-to email address*"
                centerTitle
                tooltipText="Please ensure this email has been verified by Further's team before adding it here."
              >
                <Controller
                  name="whiteLabelConfig.clientServiceEmail"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <TextField
                      {...rest}
                      inputRef={ref}
                      placeholder="What should the reply-to address be on system emails?"
                      fullWidth
                      helperText={error?.message}
                      error={!!error}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow title="Investor service email address*" centerTitle>
                <Controller
                  name="contactDetails.email"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <TextField
                      {...rest}
                      inputRef={ref}
                      placeholder="What email address should investors use to contact you?"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow title="Subdomain*" centerTitle>
                <Controller
                  name="domain"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <TextField
                      {...rest}
                      inputRef={ref}
                      placeholder="Enter text here"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            .{baseDomain}
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow title="Email footer text" topTitle>
                <Controller
                  name="whiteLabelConfig.emailFooterText"
                  control={control}
                  render={({
                    field: { value, name },
                    formState: { isSubmitting },
                    fieldState: { error },
                  }) => (
                    <>
                      <CkEditor
                        disabled={isSubmitting || !canEditFirm}
                        placeholder="Enter any footer text you need to include here, such as your limited company name, number, address or other legal information."
                        onBlur={(value: string) => {
                          setValue(name, value);
                        }}
                        value={value}
                        extraLines={1}
                        showLists={false}
                      />
                      <p className={classes.errorText}>{error?.message}</p>
                    </>
                  )}
                />
              </FieldRow>
              <FieldRow title="Portal footer text">
                <Controller
                  name="whiteLabelConfig.portalFooterText"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <TextField
                      {...rest}
                      inputRef={ref}
                      placeholder="Enter any footer text you need to include here, such as your limited company name, number, address or other legal information."
                      fullWidth
                      multiline
                      minRows={4}
                      helperText={error?.message}
                      error={!!error}
                    />
                  )}
                />
              </FieldRow>
            </Spacing>
          </PageSection>
          <Grid size={{ xs: 4 }}>
            <ButtonGroup>
              <Link to={FIRM}>
                <Button variant="outlined">Cancel</Button>
              </Link>
              {canEditFirm ? (
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  loading={isUpdating}
                >
                  Save
                </Button>
              ) : null}
            </ButtonGroup>
          </Grid>
        </Spacing>
      </form>
    </FormProvider>
  );
};

export default WhitelabellingSettings;
